/**
 * Результат подбора (§10, §16.5, экран /result/{id}).
 *
 * Три блока (тройка банков → документы → допущения) плюс блок выгоды.
 * Служебные поля («служебное», «коридор», «полная таблица», «допущения»)
 * присутствуют в ответе только у ролей брокер и выше (§16.4) — фронтенд
 * просто показывает то, что пришло, решений о доступе не принимает (§16.2).
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError, SessionExpired, api } from '../api/client'
import type {
  WhatIfField,
  WhatIfResult,
  OutcomeRecord,
  EtazhiBenefit,
  MatchResultResponse,
  ResultDocument,
  ResultOffer,
  SourcedFigure,
} from '../api/types'
import { formatDateTime, formatMoney } from '../utils/format'

// Меню «Поделиться» с файлом — только на телефоне и планшете: на компьютере
// оно открывает системное окно Windows без сохранения файла.
const canSharePdf =
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  typeof (navigator as Navigator & { canShare?: unknown }).canShare === 'function' &&
  typeof window !== 'undefined' &&
  window.matchMedia?.('(pointer: coarse)').matches === true

function OfferCard({ offer }: { offer: ResultOffer }) {
  const stale = offer['условия требуют проверки']
  return (
    <div className={`offer-card${offer.best_client ? ' best' : ''}`}>
      <div className="offer-card-head" data-label="">
        <strong>{offer.банк}</strong>
        {offer.метка != null && <span className="offer-label">{offer.метка}</span>}
        <span className="muted">{offer.программа}</span>
      </div>

      <div data-label="Ставка">{offer.ставка}%</div>
      <div data-label="Сумма кредита">{formatMoney(offer['сумма кредита'])}</div>
      <div data-label="Первоначальный взнос">{formatMoney(offer['первоначальный взнос'])}</div>
      <div data-label="Срок">{Math.round(offer['срок, мес'] / 12)} лет</div>
      <div data-label="Платёж">
        {formatMoney(offer.платёж)}/мес
        {offer['платёж со страхованием'] != null && (
          <div className="muted">со страхованием: {formatMoney(offer['платёж со страхованием'])}/мес</div>
        )}
      </div>
      <div data-label="Расходы к сделке">
        {offer['расходы к сделке'] != null ? formatMoney(offer['расходы к сделке']) : 'нет данных'}
      </div>

      {(offer.плюсы?.length || offer.риски?.length) && (
        <div className="offer-highlights" data-label="">
          {offer.плюсы?.map((text) => (
            <div key={text} className="highlight pro">
              + {text}
            </div>
          ))}
          {offer.риски?.map((text) => (
            <div key={text} className="highlight risk">
              ⚠ {text}
            </div>
          ))}
          {offer.ограничения?.map((text) => (
            <div key={text} className="highlight risk">
              − {text}
            </div>
          ))}
        </div>
      )}

      {offer.пояснения != null && offer.пояснения.length > 0 && (
        <div className="muted small" data-label="">
          {offer.пояснения.map((text) => (
            <div key={text}>{text}</div>
          ))}
        </div>
      )}

      {offer['выгода через Этажи'] != null && <EtazhiBenefitBlock benefit={offer['выгода через Этажи']} />}

      {offer['выгода через Этажи'] == null && offer['выгода через компанию'] != null && (
        <div className="benefit" data-label="">
          <strong>Выгода через компанию:</strong> {offer['выгода через компанию']['ставка через компанию']}%
          вместо {offer['выгода через компанию']['ставка напрямую']}% — экономия{' '}
          {formatMoney(offer['выгода через компанию']['экономия в месяц'])}/мес,{' '}
          {formatMoney(offer['выгода через компанию']['экономия за срок'])} за срок.{' '}
          <span className="muted">{offer['выгода через компанию'].условие}</span>
        </div>
      )}

      {offer['досрочное погашение'] != null && offer['досрочное погашение'].length > 0 && (
        <div className="benefit" data-label="">
          <strong>При доплате {formatMoney(offer['досрочное погашение'][0]['доплата в месяц'])}/мес:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {offer['досрочное погашение'].map((scenario) => (
              <li key={scenario.режим}>
                {scenario.режим}: экономия {formatMoney(scenario.экономия)}
                {scenario.режим === 'сокращение срока' && scenario['новый срок, мес'] != null && (
                  <> — новый срок {Math.round(scenario['новый срок, мес'] / 12)} лет</>
                )}
                {scenario.режим === 'сокращение платежа' && scenario['новый платёж'] != null && (
                  <> — новый платёж {formatMoney(scenario['новый платёж'])}/мес</>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {stale && <div className="notice small" data-label="">Условия банка требуют проверки — давно не обновлялись</div>}

      {offer['контакт по банку'] != null && (
        <div className="muted" data-label="Контакт">
          {offer['контакт по банку']}
        </div>
      )}

      {offer.служебное != null && (
        <details className="offer-service" data-label="">
          <summary>Служебное</summary>
          <dl>
            {Object.entries(offer.служебное).map(([key, value]) => (
              <div key={key} className="dl-row">
                <dt>{key}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  )
}

// Внутренние коды раздела из docs_matrix.yaml (mortgage_core.rules.documents)
// не предназначены для показа — только человекочитаемые подписи разделов.
const DOC_STATUSES: [string, string][] = [
  ['REQUIRED', 'нужен'],
  ['UPLOADED', 'загружен'],
  ['VERIFIED', 'проверен'],
  ['REJECTED', 'отклонён'],
  ['EXPIRED', 'просрочен'],
  ['NOT_REQUIRED', 'не требуется'],
]

// Порядок разделов чек-листа (ТЗ 3.0 §25).
const SECTION_ORDER = [
  'Заёмщик', 'Супруг', 'Созаёмщик', 'Дети', 'Доход', 'Работа', 'Объект', 'Продавец',
  'Маткапитал', 'Первоначальный взнос', 'Программа', 'Банк',
]

/** Чек-лист документов по участникам и разделам; статусы хранятся на сервере. */
function DocumentsChecklist({ documents, draftId }: { documents: ResultDocument[]; draftId: string }) {
  const [statuses, setStatuses] = useState<Record<string, string>>(() =>
    Object.fromEntries(documents.map((doc) => [doc.ключ, doc.статус])),
  )
  const [error, setError] = useState<string | null>(null)
  const bySection = useMemo(() => {
    const groups = new Map<string, ResultDocument[]>()
    for (const doc of documents) {
      const list = groups.get(doc.раздел) ?? []
      list.push(doc)
      groups.set(doc.раздел, list)
    }
    return [...groups.entries()].sort(
      ([a], [b]) => (SECTION_ORDER.indexOf(a) + 100) % 100 - (SECTION_ORDER.indexOf(b) + 100) % 100,
    )
  }, [documents])

  if (documents.length === 0) return null

  const needed = documents.filter((doc) => statuses[doc.ключ] !== 'NOT_REQUIRED')
  const ready = needed.filter((doc) => ['UPLOADED', 'VERIFIED'].includes(statuses[doc.ключ]))

  async function change(doc: ResultDocument, status: string) {
    const previous = statuses[doc.ключ]
    setStatuses((prev) => ({ ...prev, [doc.ключ]: status }))
    setError(null)
    try {
      await api.markDocument(draftId, doc.ключ, status)
    } catch (err) {
      setStatuses((prev) => ({ ...prev, [doc.ключ]: previous }))
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <section className="card">
      <h2>Документы для подачи заявки</h2>
      <p className="muted">
        Собрано {ready.length} из {needed.length}
      </p>
      {error != null && <p className="error">{error}</p>}
      {bySection.map(([section, docs]) => (
        <details key={section} open>
          <summary>{section}</summary>
          <ul className="doc-list">
            {docs.map((doc) => (
              <li key={doc.ключ} className="doc-row">
                <select value={statuses[doc.ключ]} onChange={(e) => change(doc, e.target.value)}>
                  {DOC_STATUSES.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <span>
                  {doc.название}
                  {doc.опционально && <span className="muted"> (при наличии)</span>}
                  {doc.кому !== 'заёмщик' && <span className="muted"> — {doc.кому}</span>}
                  {doc.почему != null && <span className="muted"> · {doc.почему}</span>}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </section>
  )
}

/** «Что если» (ТЗ 3.0 §18): изменить ответы и сразу увидеть новый расчёт. */
function WhatIfPanel({ draftId }: { draftId: string }) {
  const [fields, setFields] = useState<WhatIfField[] | null>(null)
  const [roles, setRoles] = useState<string[]>([])
  const [proofs, setProofs] = useState<string[]>([])
  const [values, setValues] = useState<Record<string, string>>({})
  const [addCoborrower, setAddCoborrower] = useState(false)
  const [cob, setCob] = useState({ кто: '', доход: '', форма: '' })
  const [result, setResult] = useState<WhatIfResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [terms, setTerms] = useState<Awaited<ReturnType<typeof api.whatIfTerms>> | null>(null)

  async function compareTerms() {
    setBusy(true)
    setError(null)
    try {
      setTerms(await api.whatIfTerms(draftId))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  function load() {
    if (fields != null) return
    api
      .whatIfFields(draftId)
      .then((data) => {
        setFields(data.поля)
        setRoles(data['роли созаёмщика'])
        setProofs(data['формы подтверждения'])
        setValues(Object.fromEntries(data.поля.map((f) => [f.id, f.значение == null ? '' : String(f.значение)])))
        setCob({ кто: data['роли созаёмщика'][0] ?? '', доход: '', форма: data['формы подтверждения'][0] ?? '' })
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
  }

  function changes(): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    for (const f of fields ?? []) {
      const raw = values[f.id] ?? ''
      const initial = f.значение == null ? '' : String(f.значение)
      if (raw === initial || raw === '') continue
      out[f.id] = f.тип === 'money' || f.тип === 'number' ? Number(raw.replace(/\s/g, '').replace(',', '.')) : raw
    }
    return out
  }

  async function run() {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      setResult(
        await api.whatIf(draftId, {
          изменения: changes(),
          созаёмщик: addCoborrower
            ? { кто: cob.кто, доход: Number(cob.доход.replace(/\D/g, '')), 'форма подтверждения': cob.форма }
            : null,
        }),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function saveToDraft() {
    if (result == null) return
    setError(null)
    try {
      const draft = await api.getDraft(draftId)
      await api.saveAnswers(draftId, result.изменения as never, draft.revision, 'что если')
      setSaved(true)
      // Анкета изменилась — страница результата пересчитывается по ней сразу.
      window.location.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const rows: [string, (s: WhatIfResult['сейчас']) => string][] = [
    ['Вариантов', (s) => String(s.вариантов)],
    ['Может подойти — уточнить', (s) => String(s['может подойти'])],
    ['Наибольшая сумма', (s) => (s['наибольшая сумма'] != null ? formatMoney(s['наибольшая сумма']) : '—')],
    ['Наименьший платёж', (s) => (s['наименьший платёж'] != null ? formatMoney(s['наименьший платёж']) : '—')],
    ['Подтверждённый доход', (s) => (s['подтверждённый доход'] != null ? formatMoney(s['подтверждённый доход']) : '—')],
    ['Первый в тройке', (s) => (s.тройка[0] ? `${s.тройка[0].банк}, ${s.тройка[0].ставка}%` : '—')],
  ]

  return (
    <details className="card" onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && load()}>
      <summary>
        <h2 style={{ display: 'inline' }}>Что если</h2>
      </summary>
      <p className="muted">
        Поменяйте взнос, доход, срок, стоимость, объект, продавца или сценарий — расчёт обновится рядом.
        Анкета не меняется, пока вы не нажмёте «Сохранить в анкету».
      </p>
      {fields == null && error == null && <p className="muted">Загружаю…</p>}
      {fields != null && (
        <div className="feedback-form">
          {fields.map((f) => (
            <label key={f.id} className="field-label">
              {f.подпись}
              {f.варианты.length > 0 ? (
                <select value={values[f.id] ?? ''} onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}>
                  {f.варианты.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <input
                  inputMode="numeric"
                  value={values[f.id] ?? ''}
                  onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                />
              )}
            </label>
          ))}
          <label className="option">
            <input type="checkbox" checked={addCoborrower} onChange={(e) => setAddCoborrower(e.target.checked)} />
            Добавить созаёмщика
          </label>
          {addCoborrower && (
            <>
              <select value={cob.кто} onChange={(e) => setCob({ ...cob, кто: e.target.value })}>
                {roles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <input inputMode="numeric" placeholder="Доход созаёмщика, ₽/мес" value={cob.доход}
                onChange={(e) => setCob({ ...cob, доход: e.target.value })} />
              <select value={cob.форма} onChange={(e) => setCob({ ...cob, форма: e.target.value })}>
                {proofs.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </>
          )}
          <button className="primary" disabled={busy} onClick={run}>
            {busy ? 'Считаю…' : 'Пересчитать'}
          </button>
          <button disabled={busy} onClick={compareTerms}>
            Сравнить сроки
          </button>
        </div>
      )}
      {terms != null && (
        <>
          <table className="trace-table">
            <thead>
              <tr>
                <th>Срок</th>
                <th>Лучший вариант</th>
                <th>Сумма</th>
                <th>Платёж</th>
                <th>Переплата</th>
                <th>Вариантов</th>
              </tr>
            </thead>
            <tbody>
              {terms.сроки.map((row) => (
                <tr key={row['срок, лет']}>
                  <td>
                    {row['срок, лет']} лет{row.сейчас ? ' (сейчас)' : ''}
                  </td>
                  <td>{row.лучший ? `${row.лучший.банк}, ${row.лучший.ставка}%` : '—'}</td>
                  <td>{row.лучший ? formatMoney(row.лучший.сумма) : '—'}</td>
                  <td>{row.лучший ? formatMoney(row.лучший.платёж) : '—'}</td>
                  <td>{row.лучший ? formatMoney(row.лучший.переплата) : '—'}</td>
                  <td>{row.вариантов}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">{terms.пояснение}</p>
        </>
      )}
      {error != null && <p className="error">{error}</p>}
      {result != null && (
        <>
          <table className="trace-table">
            <thead>
              <tr>
                <th></th>
                <th>Сейчас</th>
                <th>Если</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, get]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{get(result.сейчас)}</td>
                  <td>{get(result.если)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.если.тройка.length > 0 && (
            <p className="muted">
              Тройка при изменении:{' '}
              {result.если.тройка.map((o) => `${o.банк} (${o.программа}) ${o.ставка}%`).join('; ')}
            </p>
          )}
          {result.пояснения.map((note) => (
            <p key={note} className="muted">{note}</p>
          ))}
          {Object.keys(result.изменения).length > 0 && (
            <button onClick={saveToDraft} disabled={saved}>
              {saved ? 'Сохранено — пересчитываю…' : 'Сохранить в анкету'}
            </button>
          )}
        </>
      )}
    </details>
  )
}

const COPILOT_QUESTIONS: [string, string][] = [
  ['why_fits', 'Почему подходит?'],
  ['why_not', 'Почему не подходит?'],
  ['what_change', 'Что изменить?'],
  ['documents', 'Какие документы?'],
  ['etazhi', 'Выгода через Этажи'],
  ['coborrower', 'Если добавить созаёмщика'],
  ['fallback', 'Резервный сценарий'],
]

/** Помощник (ТЗ 3.0 §29): отвечает только по данным этого расчёта. */
function CopilotPanel({ draftId }: { draftId: string }) {
  const [question, setQuestion] = useState('')
  const [reply, setReply] = useState<{ ответ: string; источник: string; пометка: string | null; вопрос: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function ask(text: string) {
    if (!text.trim()) return
    setBusy(true)
    setError(null)
    try {
      setReply(await api.ask(draftId, text))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <details className="card">
      <summary>
        <h2 style={{ display: 'inline' }}>Спросить помощника</h2>
      </summary>
      <p className="muted">Ответ — только по данным этого расчёта; правила и ставки помощник не меняет.</p>
      <div className="actions">
        {COPILOT_QUESTIONS.map(([id, label]) => (
          <button key={id} disabled={busy} onClick={() => ask(id)}>
            {label}
          </button>
        ))}
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          ask(question)
        }}
      >
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Свой вопрос" />{' '}
        <button type="submit" disabled={busy}>Спросить</button>
      </form>
      {busy && <p className="muted">Думаю…</p>}
      {error != null && <p className="error">{error}</p>}
      {reply != null && (
        <div className="benefit">
          <strong>{reply.вопрос}</strong>
          <p>{reply.ответ}</p>
          <p className="muted">
            Источник: {reply.источник}
            {reply.пометка ? ` · ${reply.пометка}` : ''}
          </p>
        </div>
      )}
    </details>
  )
}

const OUTCOME_STATUSES: [string, string][] = [
  ['submitted', 'заявка подана'],
  ['approved', 'одобрено'],
  ['declined', 'отказ'],
  ['deal', 'сделка проведена'],
  ['withdrawn', 'отозвана клиентом'],
]

/** Результат заявки в банк (ТЗ 3.0 §28): статистика для директора, не правило. */
function OutcomeForm({ draftId, result }: { draftId: string; result: MatchResultResponse }) {
  const banks = Array.from(
    new Set([
      ...result.варианты.map((o) => o.банк),
      ...(result['запасные варианты'] ?? []).map((o) => o.банк),
      ...(result['может подойти — уточните'] ?? []).map((o) => o.банк),
    ]),
  )
  const [bank, setBank] = useState(banks[0] ?? '')
  const [status, setStatus] = useState('submitted')
  const [rate, setRate] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [records, setRecords] = useState<OutcomeRecord[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.outcomes(draftId).then(setRecords).catch(() => setRecords([]))
  }, [draftId])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await api.recordOutcome(draftId, {
        банк: bank,
        статус: status,
        фактическая_ставка: rate.replace(',', '.') || null,
        фактическая_сумма: amount.replace(/\D/g, '') || null,
        причина_отказа: status === 'declined' ? reason : null,
      })
      setRecords(await api.outcomes(draftId))
      setRate('')
      setAmount('')
      setReason('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <details className="card">
      <summary>
        <h2 style={{ display: 'inline' }}>Результат заявки</h2>
      </summary>
      <p className="muted">
        Что ответил банк. Сравнивается с расчётом системы и копится в статистику для директора —
        правила подбора от одного случая не меняются.
      </p>
      <form onSubmit={submit} className="feedback-form">
        <select value={bank} onChange={(e) => setBank(e.target.value)}>
          {banks.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {OUTCOME_STATUSES.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {(status === 'approved' || status === 'deal') && (
          <>
            <input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Ставка банка, %" />
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Одобренная сумма, ₽" />
          </>
        )}
        {status === 'declined' && (
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Причина отказа (если известна)" />
        )}
        <button type="submit" className="primary" disabled={!bank}>Сохранить</button>
      </form>
      {error != null && <p className="error">{error}</p>}
      {records.length > 0 && (
        <ul className="muted">
          {records.map((r) => (
            <li key={r.id}>
              {r.банк}: {r['статус текстом']}
              {r['фактическая ставка'] != null && `, ставка ${r['фактическая ставка']}%`}
              {r['расчёт системы']?.ставка != null && ` (по расчёту ${r['расчёт системы'].ставка}%)`}
              {r['причина отказа'] != null && ` — ${r['причина отказа']}`}
            </li>
          ))}
        </ul>
      )}
    </details>
  )
}

/** «Сообщить, что не так» (D-FB-01): замечание уходит директору. */
function FeedbackForm({ draftId, result }: { draftId: string; result: MatchResultResponse }) {
  const [kind, setKind] = useState('неверно')
  const [text, setText] = useState('')
  const [bank, setBank] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const banks = Array.from(new Set(result.варианты.map((o) => o.банк)))

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const response = await api.sendFeedback(draftId, {
        вид: kind,
        текст: text,
        банк: bank || null,
        контекст: { показано: result.варианты.map((o) => `${o.банк} / ${o.программа} / ${o.ставка}%`) },
      })
      setSent(response.статус)
      setText('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <details className="card">
      <summary>
        <h2 style={{ display: 'inline' }}>Сообщить, что не так</h2>
      </summary>
      <form onSubmit={submit}>
        <p className="muted">Замечание получит директор. Подтверждённое станет правилом системы.</p>
        <div className="actions">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="неверно">Результат неверный</option>
            <option value="непонятно">Непонятно, почему так</option>
            <option value="нет банка">Не хватает банка или программы</option>
            <option value="другое">Другое</option>
          </select>
          <select value={bank} onChange={(e) => setBank(e.target.value)}>
            <option value="">Банк (если про конкретный)</option>
            {banks.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          style={{ width: '100%' }}
          placeholder="Например: в Сбербанке для ПВ 35% ставка сейчас 19,9%, а не 20,5%"
        />
        <div className="actions">
          <button className="primary" type="submit" disabled={!text.trim()}>Отправить</button>
          {sent != null && <span className="muted">{sent}</span>}
          {error != null && <span className="error">{error}</span>}
        </div>
      </form>
    </details>
  )
}

export function ResultPage() {
  const { draftId } = useParams<{ draftId: string }>()
  const navigate = useNavigate()
  const [result, setResult] = useState<MatchResultResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [missing, setMissing] = useState<string[] | null>(null)
  const [downloading, setDownloading] = useState(false)
  // Отдельно от `error`: сбой скачивания PDF не должен стирать уже
  // показанный результат подбора — только всплыть рядом с кнопкой.
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [creatingNew, setCreatingNew] = useState(false)

  useEffect(() => {
    if (draftId == null) return
    setResult(null)
    setError(null)
    setMissing(null)
    api.result(draftId).catch(async (err) => {
      if (err instanceof SessionExpired) {
        window.location.href = err.portalUrl
        return
      }
      // 422: не хватает обязательных ответов для первого расчёта —
      // показать вопросы словами и вернуть к анкете.
      if (err instanceof ApiError && err.detail && typeof err.detail === 'object') {
        const detail = err.detail as { поля?: string[]; вопросы?: string[] }
        if (Array.isArray(detail.поля)) {
          setMissing(detail.вопросы?.length ? detail.вопросы : detail.поля)
          return
        }
      }
      setError(err instanceof Error ? err.message : String(err))
    }).then((data) => {
      if (data) setResult(data)
    })
  }, [draftId])

  async function createNewDraft() {
    setCreatingNew(true)
    setError(null)
    try {
      const { id } = await api.createDraft()
      navigate(`/intake/${id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setCreatingNew(false)
    }
  }

  async function downloadPdf(mode: 'save' | 'share') {
    if (draftId == null) return
    setDownloading(true)
    setPdfError(null)
    try {
      const blob = await api.resultPdf(draftId)
      const file = new File([blob], `предложение-${draftId.slice(0, 8)}.pdf`, {
        type: 'application/pdf',
      })
      // «Отправить» — системное меню «Поделиться» (§16.6): с телефона в
      // мессенджер или почтой. «Скачать» — всегда сохранение файла: на
      // компьютере с Windows меню «Поделиться» сохранить файл не даёт
      // (замечание директора 28.10.2026).
      if (mode === 'share' && canSharePdf) {
        await navigator.share({ files: [file], title: 'Предложение по ипотеке' })
        return
      }
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.name
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      if (err instanceof SessionExpired) {
        window.location.href = err.portalUrl
        return
      }
      if (err instanceof DOMException && err.name === 'AbortError') return
      setPdfError(err instanceof Error ? err.message : String(err))
    } finally {
      setDownloading(false)
    }
  }

  if (draftId == null) return null

  if (missing != null) {
    return (
      <div className="card">
        <h2>Не хватает ответов для расчёта</h2>
        <p>Чтобы увидеть результат, нужно ответить ещё на несколько вопросов анкеты.</p>
        <ul>
          {missing.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
        <div className="actions">
          <button className="primary" onClick={() => navigate(`/intake/${draftId}`)}>
            Вернуться к анкете
          </button>
        </div>
      </div>
    )
  }

  if (error != null) return <p className="error">{error}</p>
  if (result == null) return <p className="muted">Считаю…</p>

  return (
    <div>
      <div className="actions">
        <button onClick={() => navigate(`/intake/${draftId}`)}>← К анкете</button>
      </div>

      {result.предупреждение != null && <div className="notice">{result.предупреждение}</div>}

      <p className="muted">
        {result.город != null ? `Город сделки: ${result.город} · ` : ''}
        банков рассмотрено: {result['банков рассмотрено']}
      </p>

      {result['план подачи'] != null && (
        <section className="card">
          <h2>План подачи</h2>
          {result['план подачи'].внимание && (
            <p className="notice plan-warning">{result['план подачи'].внимание}</p>
          )}
          <ol>
            {result['план подачи'].шаги.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      )}

      {result.варианты.length === 0 ? (
        <div className="card">
          <p>Подходящих вариантов не найдено — все банки отклонены жёсткими фильтрами.</p>
          <p className="muted">Причины — в разделе «Не подошло» ниже.</p>
        </div>
      ) : (
        <section>
          <h2>Топ-3 варианта</h2>
          <div className="offers-grid">
            <div className="offers-head">
              <span>Банк</span>
              <span>Ставка</span>
              <span>Сумма</span>
              <span>ПВ</span>
              <span>Срок</span>
              <span>Платёж</span>
              <span>Расходы к сделке</span>
            </div>
            {result.варианты.map((offer) => (
              <OfferCard key={`${offer.банк}-${offer.программа}`} offer={offer} />
            ))}
          </div>
        </section>
      )}

      {(result['запасные варианты']?.length ?? 0) > 0 && (
        <section>
          <h2>Запасные варианты — другие банки</h2>
          <p className="muted">На случай отказа или других условий в банках тройки.</p>
          <div className="offers-grid">
            <div className="offers-head">
              <span>Банк</span>
              <span>Ставка</span>
              <span>Сумма</span>
              <span>ПВ</span>
              <span>Срок</span>
              <span>Платёж</span>
              <span>Расходы к сделке</span>
            </div>
            {(result['запасные варианты'] ?? []).map((offer) => (
              <div key={`${offer.банк}-${offer.программа}`} className="offer-wrap">
                <OfferCard offer={offer} />
                {offer['почему не в тройке'].length > 0 && (
                  <p className="muted offer-why-not">Не в тройке: {offer['почему не в тройке'].join('; ')}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {(result['подходят, но дороже']?.length ?? 0) > 0 && (
        <section className="card">
          <h2>Подходят, но дороже</h2>
          <p className="muted">
            Клиенту подходят, но заметно дороже лучшего варианта, поэтому не в тройке и не в
            запасных. Показаны, чтобы было видно: банк не пропал.
          </p>
          <ul>
            {result['подходят, но дороже']!.map((offer) => (
              <li key={`${offer.банк}-${offer.программа}`}>
                <strong>{offer.банк}</strong>, {offer.программа}: {offer.ставка}%, платёж{' '}
                {formatMoney(offer.платёж)}/мес
                {offer['дороже лучшего'] != null && (
                  <span className="muted">
                    {' '}
                    — дороже лучшего на{' '}
                    {Number(offer['дороже лучшего']['ставка, п.п.']).toLocaleString('ru-RU')} п.п.,
                    платёж больше на {formatMoney(offer['дороже лучшего'].платёж)}/мес
                  </span>
                )}
                {(offer.пояснения?.length ?? 0) > 0 && (
                  <div className="muted small">{offer.пояснения!.join('; ')}</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(result['только с субсидией застройщика']?.length ?? 0) > 0 && (
        <section className="card">
          <h2>Только с субсидией застройщика</h2>
          <p className="muted">
            Такая ставка доступна, только если застройщик субсидирует её для этого объекта.
            С обычными вариантами не сравнивается.
          </p>
          <ul>
            {result['только с субсидией застройщика']!.map((offer) => (
              <li key={`${offer.банк}-${offer.программа}`}>
                <strong>{offer.банк}</strong>, {offer.программа}: {offer.ставка}%, платёж{' '}
                {formatMoney(offer.платёж)}/мес
                <div className="muted">Условие: {offer.условие}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(result['может подойти — уточните']?.length ?? 0) > 0 && (
        <section className="card">
          <h2>Может подойти — нужно уточнить</h2>
          <p className="muted">
            Программа не отклонена: для решения не хватает ответа или проверки.
            Уточните — и вариант станет обычным.
          </p>
          <ul>
            {result['может подойти — уточните']!.map((offer) => (
              <li key={`${offer.банк}-${offer.программа}`}>
                <strong>{offer.банк}</strong>, {offer.программа}: {offer.ставка}%, платёж{' '}
                {formatMoney(offer.платёж)}/мес
                {offer['что уточнить'] != null && offer['что уточнить'].length > 0 && (
                  <div className="muted">Уточнить: {offer['что уточнить'].join('; ')}</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <DocumentsChecklist documents={result.документы} draftId={draftId} />

      <WhatIfPanel draftId={draftId} />
      <CopilotPanel draftId={draftId} />
      <OutcomeForm draftId={draftId} result={result} />
      <FeedbackForm draftId={draftId} result={result} />

      {result['требует уточнения'].length > 0 && (
        <section className="card">
          <h2>Требует уточнения</h2>
          <ul>
            {result['требует уточнения'].map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </section>
      )}

      {result['ответы «не знаю»'] != null && result['ответы «не знаю»'].length > 0 && (
        <section className="card">
          <h2>Ответы «не знаю» — на что это повлияло</h2>
          <ul>
            {result['ответы «не знаю»'].map((impact) => (
              <li key={impact.вопрос}>
                <strong>{impact.вопрос}</strong> — влияет на {impact['влияет на']}. Принято
                консервативное допущение: {impact.допущение}.
              </li>
            ))}
          </ul>
        </section>
      )}

      {(result['что изменить, чтобы подошло'] ?? []).length > 0 && (
        <div className="card">
          <h2>Что изменить, чтобы подошло больше вариантов</h2>
          <p className="muted">Каждое изменение проверено повторным расчётом.</p>
          <ul>
            {(result['что изменить, чтобы подошло'] ?? []).map((item) => (
              <li key={item.изменение}>
                <strong>{item.изменение}</strong>
                <div className="muted">
                  {item['банки и программы'].join('; ')}
                  {item['вариантов после изменения'] != null &&
                    ` — после изменения рассчитано вариантов: ${item['вариантов после изменения']}`}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result['не подошло'].length > 0 && (
        <details className="card">
          <summary>
            <h2 style={{ display: 'inline' }}>Не подошло ({result['не подошло'].length})</h2>
          </summary>
          <ul>
            {result['не подошло'].map((rejection, index) => (
              <li key={`${rejection.банк}-${rejection.программа}-${index}`}>
                <strong>
                  {rejection.банк ? `${rejection.банк}, ${rejection.программа}` : rejection.программа}
                </strong>{' '}
                — {rejection.причина}
                {(rejection['как исправить'] ?? []).length > 0
                  ? (rejection['как исправить'] ?? []).map((remedy, i) => (
                      <div className="muted" key={i}>
                        {remedy.невозможно ? 'Не исправить: ' : 'Как исправить: '}
                        {remedy.изменение}
                        {remedy['проверено пересчётом'] && remedy['новый платёж'] != null && (
                          <>
                            {' '}— пересчитано: сумма {formatMoney(remedy['новая сумма'] ?? '0')}, платёж{' '}
                            {formatMoney(remedy['новый платёж'])}
                            {remedy['новая переплата'] != null &&
                              `, переплата ${formatMoney(remedy['новая переплата'])}`}
                          </>
                        )}
                      </div>
                    ))
                  : rejection['что изменить'] != null && (
                      <div className="muted">Что изменить: {rejection['что изменить']}</div>
                    )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {(result.коридор != null || result['полная таблица'] != null) && (
        <details className="card">
          <summary>
            <h2 style={{ display: 'inline' }}>Служебная информация</h2>
          </summary>

          {result.коридор != null && (
            <div>
              <p>
                В тройке: {result.коридор['в тройке']} из 3
                {result.коридор.недобор > 0 && (
                  <span className="notice small"> — недобор {result.коридор.недобор}, порог не ослаблялся</span>
                )}
              </p>
              {result.коридор['исключено из коридора'].length > 0 && (
                <ul>
                  {result.коридор['исключено из коридора'].map((item, index) => (
                    <li key={`${item.банк}-${item.программа}-${index}`}>
                      {item.банк}, {item.программа}: {item.причина}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {result['сценарии сделки'] != null && result['сценарии сделки'].length > 0 && (
            <div>
              <h3>Сценарии сделки</h3>
              <table className="trace-table">
                <thead>
                  <tr>
                    <th>Сценарий</th>
                    <th>Вариантов</th>
                    <th>Наибольшая сумма</th>
                    <th>Наименьший платёж</th>
                    <th>Подтверждённый доход</th>
                  </tr>
                </thead>
                <tbody>
                  {result['сценарии сделки'].map((s) => (
                    <tr key={s.сценарий}>
                      <td>{s.сценарий}</td>
                      <td>{s.вариантов}</td>
                      <td>{s['наибольшая сумма кредита'] != null ? formatMoney(s['наибольшая сумма кредита']) : '—'}</td>
                      <td>{s['наименьший платёж'] != null ? formatMoney(s['наименьший платёж']) : '—'}</td>
                      <td>{s['подтверждённый доход'] != null ? formatMoney(s['подтверждённый доход']) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result['почему так решили'] != null && result['почему так решили'].length > 0 && (
            <div>
              <h3>Почему так решили</h3>
              {result['почему так решили'].map((trace) => (
                <details key={trace.программа}>
                  <summary>
                    {trace.программа} — {trace.итог}
                    {trace['вариант сделки'] && <> (вариант: {trace['вариант сделки']})</>}
                  </summary>
                  <table className="trace-table">
                    <thead>
                      <tr>
                        <th>Критерий</th>
                        <th>Факт</th>
                        <th>Требование</th>
                        <th>Источник</th>
                        <th>Результат</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trace.критерии.map((row, index) => (
                        <tr key={`${row.правило}-${index}`}>
                          <td>{row.критерий}</td>
                          <td>{row.факт ?? '—'}</td>
                          <td>{row.требование ?? '—'}</td>
                          <td>{row.источник}</td>
                          <td>
                            {row.результат}
                            {row.пояснение != null && <div className="muted">{row.пояснение}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
            </div>
          )}

          {result.допущения != null && (
            <p className="muted">
              Снимок таблицы от: {formatDateTime(result.допущения['снимок таблицы от']) ?? '—'}
              {result.допущения['вкладки требуют проверки (старше 30 дней)'].length > 0 && (
                <>
                  <br />
                  Требуют проверки: {result.допущения['вкладки требуют проверки (старше 30 дней)'].join(', ')}
                </>
              )}
              {result.допущения.версии != null && (
                <>
                  <br />
                  Версии:{' '}
                  {Object.entries(result.допущения.версии)
                    .map(([name, version]) => `${name} — ${version ?? '—'}`)
                    .join('; ')}
                </>
              )}
            </p>
          )}

          {result['документы различаются по банкам'] != null &&
            result['документы различаются по банкам'].length > 0 && (
            <div>
              <h3>Пакет документов различается между банками тройки</h3>
              <p className="muted">
                Исходный текст из таблицы — для ручной сверки, не разбит по пунктам.
              </p>
              <dl className="admin-facts">
                {result['документы различаются по банкам'].map((item) => (
                  <div key={item.банк}>
                    <dt>{item.банк}</dt>
                    <dd>{item['исходный текст']}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {result['полная таблица'] != null && result['полная таблица'].length > 0 && (
            <>
              <h3>Полная таблица всех рассчитанных вариантов</h3>
              {/* Служебная таблица — единственный экран, где разрешена
                  горизонтальная прокрутка на телефоне (§16.6). */}
              <div className="table-scroll">
                <table className="service-table">
                  <thead>
                    <tr>
                      <th>Банк</th>
                      <th>Программа</th>
                      <th>Ставка</th>
                      <th>Платёж</th>
                      <th>client_score</th>
                      <th>company_score</th>
                      <th>blended_score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result['полная таблица'].map((offer, index) => (
                      <tr key={`${offer.банк}-${offer.программа}-${index}`}>
                        <td>{offer.банк}</td>
                        <td>{offer.программа}</td>
                        <td>{offer.ставка}%</td>
                        <td>{formatMoney(offer.платёж)}</td>
                        <td>{offer.служебное?.client_score ?? '—'}</td>
                        <td>{offer.служебное?.company_score ?? '—'}</td>
                        <td>{offer.служебное?.blended_score ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </details>
      )}

      {pdfError != null && <p className="error">{pdfError}</p>}

      <div className="actions">
        <button
          className="primary"
          onClick={() => downloadPdf('save')}
          disabled={downloading || result.варианты.length === 0}
        >
          {downloading ? 'Готовлю…' : 'Скачать PDF'}
        </button>
        {canSharePdf && (
          <button onClick={() => downloadPdf('share')} disabled={downloading || result.варианты.length === 0}>
            Отправить PDF
          </button>
        )}
        <Link className="button" to={`/intake/${draftId}?edit=1`}>
          Изменить анкету
        </Link>
        <button onClick={createNewDraft} disabled={creatingNew}>
          {creatingNew ? 'Создаю…' : 'Новая анкета'}
        </button>
      </div>

      <p className="disclaimer">{result.дисклеймер}</p>
    </div>
  )
}

function figureText(figure: SourcedFigure): string {
  if (figure.значение == null) return '—'
  if (figure.единица === '%' || figure.единица === 'п.п.') return `${figure.значение} ${figure.единица}`
  return `${formatMoney(figure.значение)}${figure.единица === '₽/мес' ? '/мес' : ''}`
}

/** «Самостоятельно / через Этажи» (ТЗ 3.0 §21–23): у каждой цифры источник и дата. */
function EtazhiBenefitBlock({ benefit }: { benefit: EtazhiBenefit }) {
  const effect = benefit['экономический эффект']
  return (
    <div className="benefit" data-label="">
      <strong>Что даёт оформление через Этажи</strong>
      <table className="trace-table">
        <thead>
          <tr>
            <th></th>
            <th>Самостоятельно</th>
            <th>Через Этажи</th>
            <th>Разница</th>
          </tr>
        </thead>
        <tbody>
          {benefit.строки.map((row) => (
            <tr key={row.показатель}>
              <td>{row.показатель}</td>
              <td>{figureText(row.самостоятельно)}</td>
              <td>{figureText(row['через Этажи'])}</td>
              <td title={row.разница.допущения.join('; ')}>{figureText(row.разница)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {effect.значение != null && (
        <div>
          <strong>Экономический эффект: {formatMoney(effect.значение)}</strong>{' '}
          <span className="muted">({effect.допущения[effect.допущения.length - 1]})</span>
        </div>
      )}
      <details>
        <summary className="muted">Откуда цифры</summary>
        <ul className="muted">
          {benefit.строки.flatMap((row) => [
            <li key={`${row.показатель}-b`}>
              {row.показатель}, самостоятельно: {row.самостоятельно.источник}
              {row.самостоятельно.дата ? `, на ${row.самостоятельно.дата}` : ''} — уверенность{' '}
              {row.самостоятельно.уверенность}
            </li>,
            <li key={`${row.показатель}-e`}>
              {row.показатель}, через Этажи: {row['через Этажи'].источник} — уверенность{' '}
              {row['через Этажи'].уверенность}
            </li>,
          ])}
          {benefit.строки
            .flatMap((row) => row.разница.допущения)
            .filter((v, i, all) => all.indexOf(v) === i)
            .map((text) => (
              <li key={text}>Допущение: {text}</li>
            ))}
        </ul>
        <p className="muted">{benefit['не складывать']}</p>
      </details>
    </div>
  )
}
